import { SetStateAction, useCallback, useEffect, useState } from 'react'
import { CategoryAttribute } from '../../../types/interfaces'
import { v4 } from 'uuid'
import { maxParamNameLength, sortStep } from '../../../utils/constants'
import { FieldType, ValueType } from '../../../types/enums'

const itemPattern: CategoryAttribute = {
  id: '',
  name: '',
  fieldType: -1,
  valueType: -1,
  options: [],
  unit: -1,
  sortOrder: sortStep,
  isRequired: false,
  isVisible: true,
  externalId: '',
}

export const useForm = <T extends { attributes: CategoryAttribute[] }>(
  topData: T,
  setTopData: React.Dispatch<React.SetStateAction<T>>,
  setDisabled: React.Dispatch<SetStateAction<boolean>>,
) => {
  const [data, setData] = useState<CategoryAttribute[]>([...topData.attributes])

  const addItem = useCallback((el: CategoryAttribute) => {
    setData((prev) =>
      prev
        .toSpliced(prev.indexOf(el) + 1, 0, { ...itemPattern, id: v4(), externalId: v4() }) // +1 тут
        .map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })),
    )
  }, [])

  const removeItem = useCallback(
    (el: CategoryAttribute) =>
      setData((prev) =>
        prev
          .toSpliced(prev.indexOf(el), 1)
          .map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })),
      ),
    [],
  )

  const itemUp = useCallback(
    (el: CategoryAttribute) => {
      const i = data.indexOf(el)
      if (i <= 0) return
      const arr = [...data]
      ;[arr[i], arr[i - 1]] = [arr[i - 1], arr[i]]
      setData(arr.map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })))
    },
    [data],
  )

  const itemDown = useCallback(
    (el: CategoryAttribute) => {
      const i = data.indexOf(el)
      if (i < 0 || i >= data.length - 1) return
      const arr = [...data]
      ;[arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]
      setData(arr.map((item, idx) => ({ ...item, sortOrder: (idx + 1) * sortStep })))
    },
    [data],
  )

  const handleAttributeChange = useCallback((id: string, updates: Partial<CategoryAttribute>) => {
    setData((prev) =>
      prev.map((attr) => (String(attr.id) === String(id) ? { ...attr, ...updates } : attr)),
    )
  }, [])

  useEffect(() => {
    setTopData((prev) => ({
      ...prev,
      attributes: [
        ...data.map(({ id, ...keepAttrs }) => {
          return { ...keepAttrs }
        }),
      ],
    }))
  }, [data, setTopData])

  useEffect(() => {
    setDisabled(
      Boolean(data.find((el) => !el.name)) ||
        Boolean(data.find((el) => el.name.length > maxParamNameLength)) ||
        Boolean(data.find((el) => el.fieldType === -1)) ||
        Boolean(data.find((el) => el.fieldType !== FieldType.CHECKBOX && el.valueType === -1)) ||
        Boolean(data.find((el) => el.valueType === ValueType.NUMBER && el.unit === -1)) ||
        Boolean(
          data.find(
            (el) =>
              (el.fieldType === FieldType.MULTISELECT || el.fieldType === FieldType.SELECT) &&
              el.options.length < 2,
          ),
        ),
    )
  }, [data, setDisabled])

  return {
    data,
    addItem,
    removeItem,
    itemUp,
    itemDown,
    handleAttributeChange,
    itemPattern,
  }
}
